// The parts of sign-in that need no page: talking to the auth origin's CORS endpoints for the
// Discord relay and Ep3 code flows, polling, the discord.com probe, code clean-up, and which
// routes this load may offer. Imported by the sign-in sheet (app/signin-sheet.js) and the tests.
//
// The poll key and pair code live only in the closure of a flow object here, in the memory of the
// top Re:Vault window. Nothing is written to storage, a URL, or a message to another frame.

export const METHOD_ORDER = ["google", "microsoft", "discord", "email", "passkey", "key"];
const KNOWN = new Set(METHOD_ORDER);
// Built but not offered yet: never shown, even if a server lists it.
export const DEFERRED = new Set(["microsoft"]);

// "Google", "Google or Discord", "Google, Discord, an email link or a passkey".
const PHRASE = { google: "Google", microsoft: "Microsoft", discord: "Discord", email: "an email link", passkey: "a passkey", key: "an account key" };
export function joinOr(list, words = PHRASE) {
	const w = list.map((m) => words[m] || m);
	return w.length <= 1 ? (w[0] || "") : w.slice(0, -1).join(", ") + " or " + w[w.length - 1];
}
// Methods that are on and offered, in the sheet's order (no browser check).
export function offered(list) {
	const on = new Set((Array.isArray(list) ? list : []).filter((m) => KNOWN.has(m) && !DEFERRED.has(m)));
	return METHOD_ORDER.filter((m) => on.has(m));
}

// Same clean-up the auth service does before it hashes an Ep3 code: upper case, no spaces or
// dashes, and the letters people mistype for digits (Crockford base32 has no I, L or O).
export function normalizeEp3Code(s) {
	return String(s || "").toUpperCase().replace(/[\s-]+/g, "").replace(/[IL]/g, "1").replace(/O/g, "0").slice(0, 32);
}
export const ep3CodeComplete = (s) => /^[0-9A-Z]{8}$/.test(normalizeEp3Code(s));

// "482913" -> "482 913"
export function formatPair(pair) {
	const d = String(pair || "").replace(/\D/g, "");
	return d.length === 6 ? d.slice(0, 3) + " " + d.slice(3) : d;
}

// The methods the sheet shows, in a fixed order. `list` is what the server or the auth origin
// reported; passkeys also need the browser to support them.
export function methodsToShow(list, { passkeys = false } = {}) {
	const on = new Set((Array.isArray(list) ? list : []).filter((m) => KNOWN.has(m) && !DEFERRED.has(m)));
	if (!on.size) on.add("google");
	return METHOD_ORDER.filter((m) => on.has(m) && (m !== "passkey" || passkeys));
}

// Relay (Discord inside a Re:Vault tab) is only safe when proxied pages run on the sandbox origin:
// otherwise a proxied page shares this window's origin and could read the pair code and poll key.
// It is sign-in only: the pair code is typed on the sandbox, so for adding Discord to an account a
// page there could link its own Discord instead (the auth service refuses it too). The Ep3 code
// route works everywhere the sheet does.
export function discordRoutes({ sandboxActive = false, routes, link = false } = {}) {
	const allowed = Array.isArray(routes) && routes.length ? new Set(routes) : new Set(["direct", "relay", "ep3"]);
	const out = [];
	if (allowed.has("direct")) out.push("direct");
	if (allowed.has("relay") && sandboxActive && !link) out.push("relay");
	if (allowed.has("ep3")) out.push("ep3");
	return out;
}

// Which Discord route to suggest first: the last one that worked on this device, unless the probe
// says discord.com is blocked and the last one was direct. Every route is always shown anyway:
// a filter's block page makes the probe look successful.
export function suggestRoute(routes, { last = "", reachable = null } = {}) {
	if (!routes.length) return "";
	if (last && routes.includes(last) && !(last === "direct" && reachable === false)) return last;
	if (reachable === false) return routes.includes("relay") ? "relay" : routes.includes("ep3") ? "ep3" : routes[0];
	return routes[0];
}

// Can this browser reach discord.com directly? An opaque no-cors answer means something answered
// (Discord or a block page); a network error or no answer in `ms` means blocked. Never throws.
export async function probeDiscord({ fetchImpl = globalThis.fetch, ms = 3000 } = {}) {
	try {
		await fetchImpl("https://discord.com/favicon.ico?rv=" + Date.now().toString(36), { mode: "no-cors", credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer", signal: AbortSignal.timeout(ms) });
		return true;
	} catch {
		return false;
	}
}

// "#revault-linked=passkey" after adding a way to sign in on the auth origin.
export function linkedFromHash(hash) {
	const m = /[#&]revault-linked=([a-z]{3,12})(?:&|$)/.exec(String(hash || ""));
	return m && KNOWN.has(m[1]) ? m[1] : "";
}

export const METHOD_LABEL = { google: "Google", microsoft: "Microsoft", discord: "Discord", email: "Email", passkey: "Passkey", key: "Account key", ep3: "Discord (Ep3 code)" };

// JSON POSTs with no cookies: the auth service checks the Origin header and the poll key.
// The optional bridge has a fixed local path. Provider redirects still use the auth origin.
export function createFlowClient({ authOrigin, origin, sameOrigin = false, fetchImpl = globalThis.fetch }) {
	const local = sameOrigin === true;
	const base = local ? "/account/signin/" : String(authOrigin || "").replace(/\/+$/, "") + "/auth/flow/";
	// Firefox needs an origin referrer policy to retain the local POST Origin header.
	async function post(action, body) {
		let res;
		try {
			res = await fetchImpl(base + action, {
				method: "POST", mode: local ? "same-origin" : "cors", credentials: "omit", cache: "no-store", referrerPolicy: local ? "origin" : "no-referrer",
				...(local ? { redirect: "error" } : {}), signal: AbortSignal.timeout(10000),
				headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
			});
		} catch {
			return { status: 0, body: null };
		}
		let j = null;
		try { j = await res.json(); } catch { j = null; }
		return { status: res.status, body: j };
	}

	// Starts a relay or Ep3 flow. auth: { ticket } (sign in) or { linkTicket } (add Discord to the
	// signed-in account). Returns a flow handle or throws an Error with a message for the sheet.
	async function start(method, mode, auth = {}) {
		const body = { method, mode, origin };
		if (auth.linkTicket) { body.purpose = "link"; body.linkTicket = auth.linkTicket; }
		else if (auth.ticket) body.ticket = auth.ticket;
		const r = await post("start", body);
		if (r.status !== 200 || !r.body || typeof r.body.flow !== "string" || typeof r.body.pollKey !== "string") {
			const e = new Error(r.status === 429 ? "Too many sign-in attempts. Wait a few minutes and try again." : r.status === 0 ? "Couldn't reach the sign-in service. Check your connection and try again." : "Sign-in couldn't start. Try again.");
			e.status = r.status;
			throw e;
		}
		const id = r.body.flow;
		const pollKey = r.body.pollKey;
		const expiresAt = Date.now() + Math.max(30, Math.min(900, Number(r.body.expiresIn) || 600)) * 1000;
		const flow = {
			id, mode, method, expiresAt,
			pair: mode === "relay" ? String(r.body.pair || "") : "",
			authorizeUrl: mode === "relay" ? String(r.body.authorizeUrl || "") : "",
			poll: () => post("poll", { flow: id, pollKey }),
			redeem: (code) => post("redeem", { flow: id, pollKey, code: normalizeEp3Code(code) }),
			choose: (action) => post("choose", { flow: id, pollKey, action }),
			cancel: () => post("cancel", { flow: id, pollKey }),
		};
		return flow;
	}
	return { start };
}

// Polls a relay flow until it settles. Every 2 s, slowing to 5 s; a 429 waits longer; gives up at
// the flow's expiry. Resolves to the final answer body ({ state, ... }) or { state: "dead" }.
// `stopped()` lets the sheet cancel; `sleep` is injectable for the tests.
export async function pollFlow(flow, { stopped = () => false, sleep = (ms) => new Promise((r) => setTimeout(r, ms)), now = () => Date.now() } = {}) {
	let delay = 2000;
	let fails = 0;
	while (!stopped()) {
		if (now() >= flow.expiresAt) return { state: "dead" };
		await sleep(delay);
		if (stopped()) break;
		const r = await flow.poll();
		if (r.status === 429) { delay = Math.min(5000, delay + 1000); continue; }
		if (r.status !== 200 || !r.body) {
			// The auth origin unreachable for a moment (a Chromebook waking up): keep trying a while.
			if (++fails >= 10 || r.status === 403) return { state: "dead" };
			delay = 5000;
			continue;
		}
		fails = 0;
		const s = r.body.state;
		if (s && s !== "pending") return r.body;
		delay = Math.min(5000, delay + 500);
	}
	return { state: "cancelled" };
}

// A token from a relay or code answer. Only a JWT-shaped string counts.
export function tokenFrom(body) {
	const t = body && typeof body.token === "string" ? body.token : "";
	return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(t) && t.length < 4096 ? t : "";
}
